// Entitlement store slice types and initial state

// ─── Types ───────────────────────────────────────────────────────────

export interface Product {
    id: number;
    name: string;
}

export interface Entitlement {
    id: number;
    customer_id: number;
    product_id: number;
    product_name: string;
    customer_username: string;
    total_seats: number;
    used_seats: number;
    sub_start: string;
    sub_end: string | null;
    created_at: string;
    /** 'admin' = created by an admin, 'import' = merged from imported legacy keys. */
    source?: 'admin' | 'import';
}

export interface CreateEntitlementRequest {
    customer_id: number;
    product_id: number;
    total_seats: number;
    sub_start?: string;
    sub_end?: string | null;
}

export interface UpdateEntitlementRequest {
    total_seats?: number;
    sub_end?: string | null;
}

export interface ProvisionNanoBundleRequest {
    customer_id: number;
    seat_product_id: number;   // 15 (VML Nano) or 16 (UtilityMix Nano); the server pairs the
                               // matching update-subscription product (17 / 18) itself
    total_seats: number;
    update_sub_end: string;    // dated; the perpetual seat is always sub_end NULL
}

export interface NanoBundleResult {
    seat_entitlement_id: number;
    update_entitlement_id: number;
    seat_product_id: number;
    update_product_id: number;
    total_seats: number;
    update_sub_end: string;
}

export interface EntitlementAgent {
    id: number;
    customer_id: number;
    customer_username: string;
    agent_name: string;
    dev_uuid: string;
    last_ip: string;
    last_seen_at: string;
    status: 'online' | 'offline';
    active_leases: number;
    server_version: string;
}

export interface EntitlementLease {
    lease_id: number;
    agent_name: string;
    agent_ip: string;
    seats: number;
    granted_at: string;
    expires_at: string;
    product_id: number;
    product_name?: string;
    agent_id: number;
}

/// Activity-log filters. All optional; the backend ANDs them together.
export interface AuditLogFilters {
    agentId?: number;
    customerId?: number;
    action?: string;
}

export interface AuditLogEntry {
    id: number;
    action: string;
    resource_type: string | null;
    resource_id: string | null;
    details: Record<string, any>;
    ip_addr: string | null;
    created_at: string;
    actor_username: string | null;
}

export interface EntitlementAlarm {
    id: number;
    customer_id: number;
    customer_username: string;
    alarm_type: string;
    product_id: number;
    product_name: string;
    entitlement_id: number;
    details: Record<string, unknown>;
    is_active: boolean;
    created_at: string;
}

export interface CheckoutRequest {
    product_id: number;
    agent_id: number;
    seats?: number;
}

export interface DashboardSummary {
    active_agents: number;
    total_seats: number;
    used_seats: number;
    free_seats: number;
    active_alarms: number;
    total_customers: number;
    by_product: Array<{
        product_name: string;
        product_id: number;
        total_seats: number;
        used_seats: number;
    }>;
    by_customer: Array<{
        customer_id: number;
        username: string;
        display_name: string | null;
        entitlement_count: number;
        total_seats: number;
        used_seats: number;
        agent_count: number;
    }>;
}

// ─── Slice State Interface ───────────────────────────────────────────

export interface EntitlementState {
    entitlements: Entitlement[];
    products: Product[];
    agents: EntitlementAgent[];
    leases: EntitlementLease[];
    alarms: EntitlementAlarm[];
    auditLogs: AuditLogEntry[];
    selectedEntitlement: Entitlement | null;
    summary: DashboardSummary | null;
    isLoading: boolean;
    error: string | null;
}

// ─── Initial State ───────────────────────────────────────────────────

export const initialEntitlementState: EntitlementState = {
    entitlements: [],
    products: [],
    agents: [],
    leases: [],
    alarms: [],
    auditLogs: [],
    selectedEntitlement: null,
    summary: null,
    isLoading: false,
    error: null,
};
